import React, { useEffect, useRef } from 'react';
import { useMediaXRSession } from '../hooks/useMediaXRSession';
import MediaPanelActions from './MediaPanelActions';
import { openMediaPanel } from './MediaPanelDialogs';
import { TransformControls } from '@react-three/drei';
import * as THREE from 'three';
import { ScreenPropItem, useWorldStore } from '../stores/worldStore';
import BrowserProp from './BrowserProp';
import ScreenProp from './ScreenProp';
import RoomScreen from './RoomScreen';

export default function SceneMediaPanel({ id, item, selected, mode, orbitRef, onSelect, onDone, onChange }: {
  id: string; item: ScreenPropItem; selected: boolean; mode: 'translate' | 'rotate' | 'scale'; orbitRef: React.RefObject<any>;
  onSelect: () => void; onDone: () => void; onChange: (updates: Partial<ScreenPropItem>) => void;
}) {
  const inVR = !!useMediaXRSession();
  const group = useRef<THREE.Group>(null);
  const canBuild = useWorldStore(s => s.canBuild);
  const buildTool = useWorldStore(s => s.buildTool);
  const stream = useWorldStore(s => s.activeScreenShare);
  const snap = useWorldStore(s => s.snapSettings);
  const editable = canBuild && selected && !item.isLocked;
  const isOwner = useWorldStore(s => s.isOwner);
  useEffect(() => () => { (window as any).__transformDragging = false; if (orbitRef.current) orbitRef.current.enabled = true; }, [orbitRef]);
  // Room screens are fitted to a venue model's screen: shared content, no
  // placement handles (move them via the asset row if a venue changes).
  if (item.roomScreen) return <group position={item.position} rotation={item.rotation} scale={item.scale}>
    <RoomScreen id={id} item={item} isOwner={isOwner} />
  </group>;
  return <>
    <group ref={group} position={item.position} rotation={item.rotation} scale={item.scale} onClick={e => {
      e.stopPropagation();
      if (!inVR && item.type === 'screen-share' && !selected && buildTool === 'none' && !(window as any).__panelPlacing && !(window as any).__transformDragging) openMediaPanel(id, 'share');
    }}>
      {item.type === 'browser' ? <BrowserProp position={[0, 0, 0]} rotation={[0, 0, 0]} scale={1} url={item.browserUrl || ''} isSelected={selected} editing={editable} /> :
        <ScreenProp position={[0, 0, 0]} rotation={[0, 0, 0]} scale={1} screenType={item.type} videoUrl={item.videoUrl} streamData={item.streamData || (item.type === 'screen-share' ? stream : null)} isSelected={selected} />}
      <MediaPanelActions id={id} item={item} editing={selected} onEdit={canBuild ? onSelect : undefined} onDone={onDone} onChange={editable ? onChange : undefined} />
    </group>
    {editable && !inVR && <TransformControls object={group} mode={mode}
      showX={mode !== 'scale'} showY showZ={mode !== 'scale'}
      translationSnap={snap.grid ? snap.gridSize : null}
      rotationSnap={snap.rotation ? snap.rotationAngle * Math.PI / 180 : null}
      onMouseDown={() => { (window as any).__transformDragging = true; if (orbitRef.current) orbitRef.current.enabled = false; }}
      onMouseUp={() => {
        setTimeout(() => { (window as any).__transformDragging = false; }, 50);
        if (orbitRef.current) orbitRef.current.enabled = true;
        const g = group.current;
        if (g) {
          const scale = Math.max(0.1, Math.min(20, g.scale.y));
          g.scale.setScalar(scale);
          onChange({ position: g.position.toArray(), rotation: [g.rotation.x, g.rotation.y, g.rotation.z], scale });
        }
      }} />}
  </>;
}
